import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Alert,
  Link,
  Autocomplete,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/AssignmentTurnedIn";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard, { tableHeaderCellSx } from "./components/DataCard";
import { useCompaniesStore } from "./store/companiesStore";
import { useLicenseAgreementsStore } from "./store/licenseAgreementsStore";
import apiClient from "./utils/apiClient";
import { Link as RouterLink } from "react-router-dom";
import AccountDialog from "./projects/AccountDialog";
import { RenewalChip, dayToMs, msToDay } from "./projects/projectParts";

const STAGES = [
  "Lead Identified",
  "Initial Contact",
  "Qualification / Discovery",
  "Negotiation / Contracting",
  "Waiting for Data",
  "Waiting for Samples",
  "Waiting for Interpretation",
  "Active",
  "Closed",
];

const ACCOUNT_TYPES = ["Revenue", "Demo"];

const EMPTY_COMPANY = {
  id: "",
  name: "",
  contact_email: "",
  website: "",
  account_owner: "",
  account_type: "Demo",
  stage: "Initial Contact",
  description: "",
  technical_lead: "",
  products: [],
  employees: [],
  sso_enabled: false,
  sso_domains: [],
  sso_provider: "oauth",
  sso_provider_name: "",
  sso_client_id: "",
  sso_client_secret: "",
  sso_authorize_url: "",
  sso_token_url: "",
  sso_userinfo_url: "",
  sso_logout_url: "",
  sso_redirect_uri: "",
  sso_issuer: "",
  sso_auto_provision: false,
  sso_default_role: "",
  license_agreement_id: "",
  project_prefix: "",
};

async function suggestPrefix(name, companyId) {
  try {
    const res = await apiClient.post("/company-request", {
      action: "suggest_prefix", name, company_id: companyId || null,
    });
    return res?.records?.[0]?.prefix || "";
  } catch {
    return "";
  }
}

function csvToArray(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function arrayToCsv(value) {
  if (!value) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

export default function Companies() {
  const {
    companies,
    isLoading,
    error,
    fetchCompanies,
    addCompany,
    updateCompany,
    deleteCompany,
  } = useCompaniesStore();

  const [editing, setEditing] = useState(null);            // company being edited
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [saveError, setSaveError] = useState("");

  const { agreements, fetchAgreements } = useLicenseAgreementsStore();

  // While adding, the project prefix follows the name until the user types
  // their own; on edit, "Suggest" asks once. The server picks a prefix no
  // other company uses (company-request suggest_prefix).
  // Metalytics employees, for the account owner / technical lead / employees pickers.
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    apiClient.post("/company-request", { action: "staff" })
      .then((res) => setStaff(res?.records || []))
      .catch(() => setStaff([]));
  }, []);
  const staffName = (id) => staff.find((m) => m.id === id)?.name;

  // Onboarding, renewal and project counts per company (company-request accounts).
  const [accounts, setAccounts] = useState({});
  const [accountId, setAccountId] = useState(null);
  const loadAccounts = useCallback(async () => {
    try {
      const res = await apiClient.post("/company-request", { action: "accounts" });
      setAccounts(Object.fromEntries((res?.records || []).map((a) => [a.id, a])));
    } catch {
      setAccounts({});
    }
  }, []);
  useEffect(() => {
    loadAccounts();
  }, [companies, loadAccounts]);

  const prefixTouched = useRef(false);
  const editingName = editing?.name;
  useEffect(() => {
    if (!isAddOpen || prefixTouched.current || !editingName?.trim()) return undefined;
    const timer = setTimeout(async () => {
      const prefix = await suggestPrefix(editingName, null);
      if (!prefixTouched.current) setEditing((prev) => (prev ? { ...prev, project_prefix: prefix } : prev));
    }, 300);
    return () => clearTimeout(timer);
  }, [isAddOpen, editingName]);

  useEffect(() => {
    fetchCompanies();
    fetchAgreements();
  }, [fetchCompanies, fetchAgreements]);

  const agreementLabel = (id) => {
    const a = agreements.find((x) => x.id === id);
    return a ? `${a.title || a.id}${a.version ? ` (v${a.version})` : ""}` : id;
  };
  const defaultAgreement = agreements.find((a) => a.is_default);

  // "" in the form means "use the default"; store null, not "".
  const toPayload = (company) => ({
    ...company,
    license_agreement_id: company.license_agreement_id || null,
    project_prefix: (company.project_prefix || "").trim().toUpperCase() || null,
    account_owner: company.account_owner || null,
    technical_lead: company.technical_lead || null,
  });

  const openAdd = () => {
    setEditing({ ...EMPTY_COMPANY });
    prefixTouched.current = false;
    setSaveError("");
    setIsAddOpen(true);
  };

  const openEdit = (company) => {
    setEditing({
      ...EMPTY_COMPANY,
      ...company,
      // Backend stores arrays; surface as arrays even when DB returned null.
      products: company.products || [],
      employees: company.employees || [],
      sso_domains: company.sso_domains || [],
      license_agreement_id: company.license_agreement_id || "",
      project_prefix: company.project_prefix || "",
    });
    setSaveError("");
    setIsEditOpen(true);
  };

  const closeDialogs = () => {
    setIsAddOpen(false);
    setIsEditOpen(false);
    setEditing(null);
    setSaveError("");
  };

  const handleField = (field, value) =>
    setEditing((prev) => ({ ...prev, [field]: value }));

  const saveAdd = async () => {
    if (!editing?.id?.trim()) {
      setSaveError("Company id is required.");
      return;
    }
    if (!editing?.sso_domains?.length) {
      setSaveError("At least one email domain is required.");
      return;
    }
    try {
      await addCompany(toPayload(editing));
      closeDialogs();
    } catch (e) {
      setSaveError(e?.message || "Failed to create company");
    }
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    try {
      await updateCompany(editing.id, toPayload(editing));
      closeDialogs();
    } catch (e) {
      setSaveError(e?.message || "Failed to update company");
    }
  };

  const [deleteError, setDeleteError] = useState("");
  const handleDelete = async () => {
    setDeleteError("");
    try {
      await deleteCompany(deleteId);
    } catch (e) {
      setDeleteError(e?.message || "Could not delete the company");
    } finally {
      setDeleteId(null);
    }
  };

  // SSO accordion is shared between Add + Edit dialogs.
  const renderSsoSection = () => (
    <Accordion sx={{ mt: 2 }} defaultExpanded={!!editing?.sso_enabled}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LockIcon fontSize="small" />
          <Typography variant="subtitle1">Single Sign-On (SSO)</Typography>
          {editing?.sso_enabled && (
            <Chip label="enabled" size="small" color="success" sx={{ ml: 1 }} />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <FormControlLabel
          control={
            <Switch
              checked={!!editing?.sso_enabled}
              onChange={(_, checked) => handleField("sso_enabled", checked)}
            />
          }
          label="Enable SSO for this company"
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            label="Provider"
            value="oauth"
            margin="normal"
            InputProps={{ readOnly: true }}
            helperText="Generic OAuth2/OIDC. Vendor (Azure/Okta/Google/etc) is inferred from the URLs below."
          />
          <TextField
            label="Provider Display Name"
            value={editing?.sso_provider_name || ""}
            onChange={(e) => handleField("sso_provider_name", e.target.value)}
            margin="normal"
            placeholder="Sign in with Acme"
          />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            label="Client ID"
            value={editing?.sso_client_id || ""}
            onChange={(e) => handleField("sso_client_id", e.target.value)}
            margin="normal"
          />
          <TextField
            label="Client Secret"
            type="password"
            value={editing?.sso_client_secret || ""}
            onChange={(e) => handleField("sso_client_secret", e.target.value)}
            margin="normal"
            helperText="Stored encrypted at rest"
          />
        </Box>
        <TextField
          label="Authorize URL"
          value={editing?.sso_authorize_url || ""}
          onChange={(e) => handleField("sso_authorize_url", e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Token URL"
          value={editing?.sso_token_url || ""}
          onChange={(e) => handleField("sso_token_url", e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="UserInfo URL"
          value={editing?.sso_userinfo_url || ""}
          onChange={(e) => handleField("sso_userinfo_url", e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Logout URL"
          value={editing?.sso_logout_url || ""}
          onChange={(e) => handleField("sso_logout_url", e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Issuer"
          value={editing?.sso_issuer || ""}
          onChange={(e) => handleField("sso_issuer", e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Redirect URI"
          value={editing?.sso_redirect_uri || ""}
          onChange={(e) => handleField("sso_redirect_uri", e.target.value)}
          fullWidth
          margin="normal"
          helperText="Frontend callback URL registered with the IdP"
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 2, alignItems: "center" }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!editing?.sso_auto_provision}
                onChange={(_, checked) => handleField("sso_auto_provision", checked)}
              />
            }
            label="Auto-provision new users"
          />
          <TextField
            label="Default role for auto-provisioned users"
            value={editing?.sso_default_role || ""}
            onChange={(e) => handleField("sso_default_role", e.target.value)}
            margin="normal"
            helperText="Leave blank if users should be created with no roles (continuous_mfa uses product entitlements, not roles, for most permissions)."
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderCommonFields = (isAdd) => (
    <>
      {isAdd && (
        <TextField
          label="Company ID"
          value={editing?.id || ""}
          onChange={(e) => handleField("id", e.target.value)}
          fullWidth
          margin="normal"
          required
          helperText="Short slug — used as primary key. Lowercase, no spaces."
        />
      )}
      <TextField
        label="Name"
        value={editing?.name || ""}
        onChange={(e) => handleField("name", e.target.value)}
        fullWidth
        margin="normal"
      />
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <TextField
          label="Contact Email"
          value={editing?.contact_email || ""}
          onChange={(e) => handleField("contact_email", e.target.value)}
          margin="normal"
        />
        <TextField
          label="Website"
          value={editing?.website || ""}
          onChange={(e) => handleField("website", e.target.value)}
          margin="normal"
        />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        {[["account_owner", "Account Owner"], ["technical_lead", "Technical Lead"]].map(([field, label]) => {
          const value = editing?.[field] || "";
          const stale = value && !staffName(value);
          return (
            <TextField
              key={field}
              select
              label={label}
              value={value}
              onChange={(e) => handleField(field, e.target.value || null)}
              margin="normal"
              error={!!stale}
              helperText={stale ? "Not a Metalytics employee: pick again" : "A Metalytics employee"}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {stale && <MenuItem value={value}>{value}</MenuItem>}
              {staff.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
            </TextField>
          );
        })}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <Select
          value={editing?.account_type || "Demo"}
          onChange={(e) => handleField("account_type", e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
        >
          {ACCOUNT_TYPES.map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </Select>
        <Select
          value={editing?.stage || "Initial Contact"}
          onChange={(e) => handleField("stage", e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
        >
          {STAGES.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </Select>
      </Box>
      <TextField
        label="Renewal date"
        type="date"
        value={msToDay(editing?.renewal_date)}
        onChange={(e) => handleField("renewal_date", dayToMs(e.target.value))}
        margin="normal"
        InputLabelProps={{ shrink: true }}
        helperText="Annual renewal. Flagged 60 days ahead (red at 14) on this page and the Projects dashboard."
      />
      <TextField
        label="Description"
        value={editing?.description || ""}
        onChange={(e) => handleField("description", e.target.value)}
        fullWidth
        multiline
        minRows={2}
        margin="normal"
      />
      <TextField
        label="Products (comma-separated)"
        value={arrayToCsv(editing?.products)}
        onChange={(e) => handleField("products", csvToArray(e.target.value))}
        fullWidth
        margin="normal"
      />
      <Autocomplete
        multiple
        options={staff.map((m) => m.id)}
        value={editing?.employees || []}
        onChange={(_, v) => handleField("employees", v)}
        getOptionLabel={(id) => staffName(id) || id}
        renderTags={(ids, getTagProps) => ids.map((id, i) => {
          const { key, ...rest } = getTagProps({ index: i });
          return <Chip key={key} {...rest} size="small" label={staffName(id) || id}
                       color={staffName(id) ? "default" : "warning"} />;
        })}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Employees"
            margin="normal"
            helperText={(editing?.employees || []).some((id) => !staffName(id))
              ? "Orange ones aren't Metalytics employees: remove them"
              : "The Metalytics employees on this account"}
          />
        )}
      />
      <TextField
        label="Email Domains (comma-separated)"
        value={arrayToCsv(editing?.sso_domains)}
        onChange={(e) => handleField("sso_domains", csvToArray(e.target.value).map((d) => d.toLowerCase()))}
        fullWidth
        margin="normal"
        required={isAdd}
        helperText="The email domains of this company's users, e.g. bms.com, bms-uk.com. A user belongs to the company that owns their domain: it decides their license agreement and their projects, whether or not the company uses SSO. Each domain belongs to one company."
      />
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <TextField
          label="Project ID prefix"
          value={editing?.project_prefix || ""}
          onChange={(e) => {
            prefixTouched.current = true;
            handleField("project_prefix", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10));
          }}
          margin="normal"
          sx={{ flex: 1 }}
          InputProps={{ readOnly: (editing?.project_seq || 0) > 0 }}
          helperText={(editing?.project_seq || 0) > 0
            ? `Locked: project IDs up to ${editing.project_prefix}-${String(editing.project_seq).padStart(3, "0")} have been issued.`
            : `Project IDs will read ${editing?.project_prefix || "PREFIX"}-001, -002… 2-10 letters or digits, starting with a letter.`}
        />
        {!isAdd && !((editing?.project_seq || 0) > 0) && (
          <Button
            sx={{ mt: 3 }}
            onClick={async () => handleField("project_prefix", await suggestPrefix(editing?.name || "", editing?.id))}
          >
            Suggest
          </Button>
        )}
      </Box>
      <TextField
        select
        label="License Agreement"
        value={editing?.license_agreement_id || ""}
        onChange={(e) => handleField("license_agreement_id", e.target.value)}
        fullWidth
        margin="normal"
        helperText="Users at the domains above must accept this agreement on login (replacing the default)."
      >
        <MenuItem value="">
          <em>Default{defaultAgreement ? ` — ${agreementLabel(defaultAgreement.id)}` : ""}</em>
        </MenuItem>
        {agreements
          .filter((a) => !a.is_default && (a.active !== false || a.id === editing?.license_agreement_id))
          .map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {agreementLabel(a.id)} — {a.id}{a.active === false ? " (inactive)" : ""}
            </MenuItem>
          ))}
      </TextField>
      {renderSsoSection()}
      {saveError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSaveError("")}>
          {saveError}
        </Alert>
      )}
    </>
  );

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<BusinessIcon />}
          title="Companies"
          subtitle="Manage customer companies, account stage, license agreement, and per-company SSO"
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openAdd}
            >
              Add Company
            </Button>
          }
        />
        {deleteError && <Alert severity="error" onClose={() => setDeleteError("")}>{deleteError}</Alert>}
        <DataCard noPadding>
          {isLoading && (
            <Box sx={{ p: 3, color: "text.secondary" }}>Loading companies…</Box>
          )}
          {error && (
            <Box sx={{ p: 3, color: "error.main" }}>Error: {error}</Box>
          )}
          <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderCellSx}>ID</TableCell>
                <TableCell sx={tableHeaderCellSx}>Name</TableCell>
                <TableCell sx={tableHeaderCellSx}>Account Type</TableCell>
                <TableCell sx={tableHeaderCellSx}>Stage</TableCell>
                <TableCell sx={tableHeaderCellSx}>SSO</TableCell>
                <TableCell sx={tableHeaderCellSx}>Email Domains</TableCell>
                <TableCell sx={tableHeaderCellSx}>Project Prefix</TableCell>
                <TableCell sx={tableHeaderCellSx}>Onboarding</TableCell>
                <TableCell sx={tableHeaderCellSx}>Renewal</TableCell>
                <TableCell sx={tableHeaderCellSx}>Open Projects</TableCell>
                <TableCell sx={tableHeaderCellSx}>License Agreement</TableCell>
                <TableCell sx={tableHeaderCellSx} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies && companies.map((company) => (
                <TableRow key={company.id} hover>
                  <TableCell>{company.id}</TableCell>
                  <TableCell>{company.name}</TableCell>
                  <TableCell>{company.account_type}</TableCell>
                  <TableCell>{company.stage}</TableCell>
                  <TableCell>
                    {company.sso_enabled ? (
                      <Chip label="enabled" size="small" color="success" />
                    ) : (
                      <Chip label="off" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {company.sso_domains && company.sso_domains.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 260 }}>
                        {company.sso_domains.slice(0, 3).map((domain) => (
                          <Chip key={domain} label={domain} size="small" variant="outlined" />
                        ))}
                        {company.sso_domains.length > 3 && (
                          <Tooltip title={company.sso_domains.slice(3).join(", ")}>
                            <Chip label={`+${company.sso_domains.length - 3}`} size="small" variant="outlined" />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {company.project_prefix || (
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {accounts[company.id] ? `${accounts[company.id].onboarding_done} of ${accounts[company.id].onboarding_total}` : "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {company.renewal_date ? (
                      <>
                        <Typography variant="body2">{msToDay(company.renewal_date)}</Typography>
                        <RenewalChip days={accounts[company.id]?.renewal_days} />
                      </>
                    ) : (
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {accounts[company.id]?.active_projects ? (
                      <Link component={RouterLink} to={`/projects?company=${encodeURIComponent(company.id)}`}>
                        {accounts[company.id].active_projects}
                      </Link>
                    ) : "0"}
                  </TableCell>
                  <TableCell>
                    {company.license_agreement_id ? (
                      <Chip label={agreementLabel(company.license_agreement_id)} size="small" color="primary" variant="outlined" />
                    ) : (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>Default</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Account: onboarding, renewal, usage">
                      <IconButton size="small" color="primary" aria-label={`Account for ${company.name || company.id}`}
                                  onClick={() => setAccountId(company.id)}>
                        <AssignmentIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Company">
                      <IconButton size="small" color="primary" onClick={() => openEdit(company)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Company">
                      <IconButton size="small" onClick={() => setDeleteId(company.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        </DataCard>
      </PageContainer>

      <AccountDialog companyId={accountId} onClose={() => setAccountId(null)} onChanged={loadAccounts} />

      {/* Add Dialog */}
      <Dialog open={isAddOpen} onClose={closeDialogs} maxWidth="md" fullWidth>
        <DialogTitle>Add Company</DialogTitle>
        <DialogContent>{editing && renderCommonFields(true)}</DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs}>Cancel</Button>
          <Button onClick={saveAdd} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onClose={closeDialogs} maxWidth="md" fullWidth>
        <DialogTitle>Edit Company — {editing?.id}</DialogTitle>
        <DialogContent>{editing && renderCommonFields(false)}</DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs}>Cancel</Button>
          <Button onClick={saveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Company?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete company <strong>{deleteId}</strong>? This cannot be undone.
            Users at this company's SSO domain will fall back to password
            login until a new company record is created.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
